import {
  UC_FLOOR_RATE_USD,
  UC_PAYG_RATE_USD,
  UC_TIERS,
  TIER_ENTITLEMENTS,
  type PricingTierId,
} from '@syncanix/shared';

import { postedInteractionsForUsageCredits } from '@/features/uc-calculator';

/**
 * Feature-comparison matrix data (ADR-0149) — the typed SOT for the per-tier
 * feature table on the marketing `/pricing` page.
 *
 * Every NUMERIC / availability cell is derived from a shared pricing SOT
 * (`@syncanix/shared`), never re-typed, so a card/entitlement edit can never
 * silently drift the matrix (`feature-matrix-data.test.ts` re-derives from the
 * SOT and pins every such cell):
 *
 *   - included UC + seats            → `UC_TIERS`
 *   - interactions per month         → `postedInteractionsForUsageCredits` (ADR-0442: allowance ÷ 3 … ÷ 12)
 *   - conversation retention (days)  → `TIER_ENTITLEMENTS.conversationRetentionDays`
 *   - server-side execution / branding removal / Frontier → `TIER_ENTITLEMENTS.channelB` / `.brandingRemoval` / `.frontier`
 *   - pay-as-you-go / floor $/UC     → `UC_PAYG_RATE_USD` / `UC_FLOOR_RATE_USD`
 *
 * Rows with NO machine-readable per-tier SOT are deliberately EXCLUDED rather
 * than guessed (BYOK-per-tier — already covered by the neighbouring route
 * matrix and conflicting between `pricing.md` and the API tier catalog; custom
 * theming; drift detection; BYOMCP; SDKs; workspace / insight caps that live in
 * `apps/api` and are not importable here; per-tier DPA / audit-retention).
 *
 * Uniform platform capabilities that carry no tier gate (chat widget, MCP
 * server, multilingual ×6, act-as-user, confirmations + step-up, EU residency,
 * RLS tenant isolation) are shown available on every tier — the honest state:
 * no gate = available to all. Pure data (no React, no `t`); labels are i18n keys
 * resolved in the component.
 */

export const FEATURE_MATRIX_TIERS: readonly PricingTierId[] = [
  'starter',
  'growth',
  'scale',
  'enterprise',
];

/** A single tier cell: a boolean availability mark, or a value (verbatim or keyed). */
export type FeatureCell =
  | { readonly kind: 'bool'; readonly value: boolean }
  | { readonly kind: 'value'; readonly text?: string; readonly textKey?: string };

export interface FeatureRow {
  /** Stable id for tests + `key`. */
  readonly id: string;
  readonly labelKey: string;
  /** Optional sub-label / footnote clarifying the row (e.g. units, caveats). */
  readonly hintKey?: string;
  readonly cells: Readonly<Record<PricingTierId, FeatureCell>>;
}

export interface FeatureSection {
  readonly id: string;
  readonly titleKey: string;
  readonly rows: readonly FeatureRow[];
}

/** Verbatim thousands-grouped integer (en-US) — matches the tier cards' number style. */
const INT_FMT = new Intl.NumberFormat('en-US');
const fmtInt = (n: number): string => INT_FMT.format(n);

const bool = (value: boolean): FeatureCell => ({ kind: 'bool', value });
const val = (text: string): FeatureCell => ({ kind: 'value', text });
const custom = (): FeatureCell => ({
  kind: 'value',
  textKey: 'pricing.featureMatrix.value.custom',
});
const keyed = (textKey: string): FeatureCell => ({ kind: 'value', textKey });

function perTier(fn: (tier: PricingTierId) => FeatureCell): Record<PricingTierId, FeatureCell> {
  return {
    starter: fn('starter'),
    growth: fn('growth'),
    scale: fn('scale'),
    enterprise: fn('enterprise'),
  };
}

const ALL_TIERS_YES = perTier(() => bool(true));

/** Included UC per tier; Enterprise is a custom committed block (`includedUsageCredits === -1`). */
const includedUcCell = (tier: PricingTierId): FeatureCell => {
  const uc = UC_TIERS[tier].includedUsageCredits;
  return uc > 0 ? val(fmtInt(uc)) : custom();
};

/**
 * Interactions/mo — **exact**, as a range, not an estimate (ADR-0442, repriced
 * by ADR-0776).
 *
 * Under the retired measured model this cell could only ever be "≈ allowance ÷ a
 * typical weight". A posted price is one of exactly four values, so an allowance
 * buys a bracket with hard endpoints: every interaction at the 6-UC answer cell,
 * or every one at the 30-UC top. Growth's 110,000 UC is therefore 18,333 / 3,666,
 * and any real month lands between them. Both endpoints are DERIVED from
 * `POSTED_PRICE_SET`, so a repricing moves this cell without anyone editing it.
 *
 * The cell renders NUMBERS ONLY (`18,333 / 3,666`), like every other numeric
 * cell in the matrix, and the row's localized hint says which end is which — a
 * verbatim "simple / full" would ship two English words into six locales.
 */
const interactionsCell = (tier: PricingTierId): FeatureCell => {
  const uc = UC_TIERS[tier].includedUsageCredits;
  if (uc <= 0) return custom();
  const { simple, full } = postedInteractionsForUsageCredits(uc);
  return val(`${fmtInt(simple)} / ${fmtInt(full)}`);
};

/** Seats per tier; Enterprise is custom (`seatCap === -1`). */
const seatsCell = (tier: PricingTierId): FeatureCell => {
  const seats = UC_TIERS[tier].seatCap;
  return seats > 0 ? val(fmtInt(seats)) : custom();
};

/** Opt-in $/UC beyond the included allowance: PAYG for self-serve, the negotiated floor for Enterprise. */
const overageCell = (tier: PricingTierId): FeatureCell =>
  val(`$${tier === 'enterprise' ? UC_FLOOR_RATE_USD : UC_PAYG_RATE_USD}/UC`);

const retentionCell = (tier: PricingTierId): FeatureCell =>
  val(fmtInt(TIER_ENTITLEMENTS[tier].conversationRetentionDays));

export const FEATURE_MATRIX_SECTIONS: readonly FeatureSection[] = [
  {
    id: 'usage',
    titleKey: 'pricing.featureMatrix.section.usage',
    rows: [
      {
        id: 'includedUc',
        labelKey: 'pricing.featureMatrix.row.includedUc.label',
        hintKey: 'pricing.featureMatrix.row.includedUc.hint',
        cells: perTier(includedUcCell),
      },
      {
        id: 'interactions',
        labelKey: 'pricing.featureMatrix.row.interactions.label',
        hintKey: 'pricing.featureMatrix.row.interactions.hint',
        cells: perTier(interactionsCell),
      },
      {
        id: 'seats',
        labelKey: 'pricing.featureMatrix.row.seats.label',
        cells: perTier(seatsCell),
      },
      {
        id: 'overage',
        labelKey: 'pricing.featureMatrix.row.overage.label',
        hintKey: 'pricing.featureMatrix.row.overage.hint',
        cells: perTier(overageCell),
      },
    ],
  },
  {
    id: 'product',
    titleKey: 'pricing.featureMatrix.section.product',
    rows: [
      {
        id: 'widget',
        labelKey: 'pricing.featureMatrix.row.widget.label',
        cells: ALL_TIERS_YES,
      },
      {
        id: 'mcp',
        labelKey: 'pricing.featureMatrix.row.mcp.label',
        cells: ALL_TIERS_YES,
      },
      {
        id: 'multilingual',
        labelKey: 'pricing.featureMatrix.row.multilingual.label',
        cells: ALL_TIERS_YES,
      },
      {
        id: 'actAsUser',
        labelKey: 'pricing.featureMatrix.row.actAsUser.label',
        cells: ALL_TIERS_YES,
      },
      {
        id: 'confirmations',
        labelKey: 'pricing.featureMatrix.row.confirmations.label',
        cells: ALL_TIERS_YES,
      },
      {
        id: 'channelB',
        labelKey: 'pricing.featureMatrix.row.channelB.label',
        hintKey: 'pricing.featureMatrix.row.channelB.hint',
        cells: perTier((tier) => bool(TIER_ENTITLEMENTS[tier].channelB)),
      },
      {
        id: 'branding',
        labelKey: 'pricing.featureMatrix.row.branding.label',
        cells: perTier((tier) => bool(TIER_ENTITLEMENTS[tier].brandingRemoval)),
      },
      {
        id: 'frontier',
        labelKey: 'pricing.featureMatrix.row.frontier.label',
        hintKey: 'pricing.featureMatrix.row.frontier.hint',
        // ADR-0464 — Frontier is UNIVERSAL. It was bundled into Growth+ per
        // ADR-0182 and read `TIER_ENTITLEMENTS[tier].frontier`; that field was
        // removed when Frontier became on-for-every-tenant, billed through
        // normal usage rather than a tier gate. The row stays (it is a real
        // selling point) and now says what is true: included everywhere.
        cells: ALL_TIERS_YES,
      },
    ],
  },
  {
    id: 'trust',
    titleKey: 'pricing.featureMatrix.section.trust',
    rows: [
      {
        id: 'euResidency',
        labelKey: 'pricing.featureMatrix.row.euResidency.label',
        cells: ALL_TIERS_YES,
      },
      {
        id: 'rls',
        labelKey: 'pricing.featureMatrix.row.rls.label',
        cells: ALL_TIERS_YES,
      },
      {
        id: 'retention',
        labelKey: 'pricing.featureMatrix.row.retention.label',
        hintKey: 'pricing.featureMatrix.row.retention.hint',
        cells: perTier(retentionCell),
      },
    ],
  },
  {
    id: 'support',
    titleKey: 'pricing.featureMatrix.section.support',
    rows: [
      {
        id: 'support',
        labelKey: 'pricing.featureMatrix.row.support.label',
        cells: {
          starter: keyed('pricing.featureMatrix.support.email'),
          growth: keyed('pricing.featureMatrix.support.priority'),
          scale: keyed('pricing.featureMatrix.support.sla'),
          enterprise: keyed('pricing.featureMatrix.support.csm'),
        },
      },
    ],
  },
];

/** Find a row by id across all sections (test + narrow consumers). */
export function findFeatureRow(id: string): FeatureRow | undefined {
  for (const section of FEATURE_MATRIX_SECTIONS) {
    const row = section.rows.find((r) => r.id === id);
    if (row) return row;
  }
  return undefined;
}
